import React from 'react';
import { useInView } from '../hooks/useInView';

const aiFeatures = [
  { icon: '◎', title: 'Vision Intake', desc: 'Per-field confidence over scanned & legacy PDFs; every row human-verified before use.', color: '#7B3FE4' },
  { icon: '⌗', title: 'Verdict Narration', desc: 'Plain-language explanation of the engine\'s exceptions — never influencing them.', color: '#00C9B1' },
  { icon: '✨', title: 'Quality Assistant', desc: 'Read-only natural-language Q&A across batch records and audit history.', color: '#4C6EF5' },
  { icon: '◈', title: 'RCA / CAPA Hints', desc: 'AI-suggested root cause and corrective actions for reviewers to weigh.', color: '#F5A623' },
  { icon: '↻', title: 'Continuous Learning', desc: 'Offline, change-controlled improvement — never live-tuning a regulated decision.', color: '#00B894' },
  { icon: '⧉', title: 'Non-authoritative', desc: 'Engine + e-signatures decide; AI switches fully off (GXP_DISABLE_LLM) for validated builds.', color: '#E74C3C', badge: 'Governed' },
];

export default function AISection() {
  const [ref, inView] = useInView();

  return (
    <section id="ai" style={{ padding: '120px 24px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div ref={ref} style={{
          marginBottom: 72,
          opacity: inView ? 1 : 0,
          transform: inView ? 'translateY(0)' : 'translateY(24px)',
          transition: 'opacity 0.6s ease, transform 0.6s ease',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 60, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 340px' }}>
              <p style={{
                fontSize: 12, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase',
                color: '#7B3FE4', marginBottom: 14, fontFamily: "'JetBrains Mono', monospace",
              }}>AI, Governed</p>
              <h2 style={{
                fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 900, lineHeight: 1.08,
                letterSpacing: '-0.03em', color: '#fff', marginBottom: 20,
              }}>AI everywhere it helps.</h2>
              <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, marginBottom: 28 }}>
                Five AI touchpoints accelerate review — none ever holds authority over a disposition.
              </p>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 10,
                background: 'rgba(123,63,228,0.12)',
                border: '1px solid rgba(123,63,228,0.25)',
                borderRadius: 10, padding: '12px 18px',
              }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#00C9B1', animation: 'pulse 2s infinite' }} />
                <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
                  The Quality Assistant is read-only — it can surface records but never edit, transition, or release one.
                </span>
              </div>
            </div>

            {/* Visual: AI shield */}
            <div style={{
              flex: '0 0 auto',
              width: 220, height: 220,
              position: 'relative',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {/* Concentric rings */}
              {[200, 160, 120].map((size, i) => (
                <div key={size} style={{
                  position: 'absolute',
                  width: size, height: size,
                  borderRadius: '50%',
                  border: `1px solid rgba(123,63,228,${0.15 - i * 0.04})`,
                  animation: `spin ${8 + i * 4}s linear infinite ${i % 2 === 0 ? '' : 'reverse'}`,
                }} />
              ))}
              <div style={{
                width: 80, height: 80, borderRadius: '50%',
                background: 'linear-gradient(135deg, #7B3FE4, #00C9B1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 32, boxShadow: '0 0 40px rgba(123,63,228,0.4)',
                zIndex: 1,
              }}>✨</div>
            </div>
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 16,
        }}>
          {aiFeatures.map((f, i) => {
            const [fRef, fInView] = useInView();
            return (
              <div key={f.title} ref={fRef} style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 14, padding: '22px',
                opacity: fInView ? 1 : 0,
                transform: fInView ? 'translateY(0)' : 'translateY(20px)',
                transition: `opacity 0.5s ease ${i * 70}ms, transform 0.5s ease ${i * 70}ms`,
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = f.color + '30'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; }}
              >
                <div style={{
                  width: 38, height: 38, borderRadius: 9,
                  background: f.color + '15', border: `1px solid ${f.color}20`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, color: f.color, marginBottom: 14,
                }}>{f.icon}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{f.title}</h4>
                  {f.badge && (
                    <span style={{
                      fontSize: 9, fontWeight: 800, background: '#9B59B6', color: '#fff',
                      padding: '2px 7px', borderRadius: 100, textTransform: 'uppercase',
                    }}>{f.badge}</span>
                  )}
                </div>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', lineHeight: 1.65 }}>{f.desc}</p>
              </div>
            );
          })}
        </div>

        {/* Bottom statement */}
        <div style={{
          marginTop: 40,
          background: 'linear-gradient(135deg, rgba(123,63,228,0.08), rgba(0,201,177,0.08))',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: 16, padding: '20px 28px',
        }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#7B3FE4', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Governed AI</span>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', marginTop: 6 }}>
            AI assists everywhere it helps and holds authority nowhere it shouldn't — the deterministic engine and human e-signatures remain the only authorities.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.3; } }
      `}</style>
    </section>
  );
}
